/**
 * Buscador de rutina — "Descubre tu rutina"
 *
 * generateRoutineRecommendation(answers) is the only function that knows the
 * recommendation rules. It is deterministic and has no side effects, so it
 * can later be replaced by an async call to an AI/API service — the UI
 * controller below only needs a promise that resolves to the same shape:
 *   { introText: string, steps: [{ key, label, description }] }
 */

var STEP_LABELS = {
  limpieza: 'Limpieza',
  tonico: 'Tónico / esencia',
  serum: 'Sérum',
  hidratante: 'Hidratante',
  spf: 'Protección solar (SPF)'
};

var STEP_BASE_DESCRIPTIONS = {
  limpieza: 'Un limpiador suave para retirar impurezas sin resecar la piel.',
  tonico: 'Un tónico o esencia para preparar la piel y aportar una primera capa de confort.',
  spf: 'Protección solar diaria, el paso que más cuida la piel a largo plazo.'
};

var HIDRATANTE_BY_SKIN_TYPE = {
  seca: 'Una crema hidratante más rica en textura, pensada para reforzar una piel con tendencia a la sequedad.',
  grasa: 'Una crema hidratante ligera, para hidratar sin sobrecargar una piel con tendencia a la grasa.',
  mixta: 'Una crema hidratante equilibrada, adaptada a una piel mixta.',
  normal: 'Una crema hidratante adaptada a tu tipo de piel.',
  no_seguro: 'Una crema hidratante de textura equilibrada, un buen punto de partida mientras conoces mejor tu piel.'
};

var SERUM_FOCUS_BY_CONCERN = {
  manchas: 'la luminosidad y el tono uniforme de la piel',
  imperfecciones: 'la piel con imperfecciones, sin agredirla',
  poros: 'la textura y la apariencia de los poros',
  hidratacion: 'la hidratación profunda',
  antiedad: 'la firmeza y la nutrición',
  sensibilidad: 'la calma de una piel reactiva',
  glow: 'la luminosidad',
  barrera: 'el refuerzo de la barrera cutánea'
};

var SKIN_TYPE_PHRASES = {
  seca: 'una piel que tiende a la sequedad',
  mixta: 'una piel mixta',
  grasa: 'una piel con tendencia a la grasa',
  normal: 'una piel bastante equilibrada',
  no_seguro: 'una piel que todavía estás conociendo'
};

var CONCERN_PHRASES = {
  manchas: 'trabajar la luminosidad y las manchas',
  imperfecciones: 'cuidar las imperfecciones sin agredir la piel',
  poros: 'afinar la textura y minimizar la apariencia de los poros',
  hidratacion: 'recuperar confort e hidratación',
  antiedad: 'aportar firmeza y nutrición',
  sensibilidad: 'calmar y proteger una piel reactiva',
  glow: 'ganar luminosidad',
  barrera: 'reforzar la barrera cutánea'
};

var GOAL_PHRASES = {
  hidratacion: 'salir con la piel más cómoda y nutrida',
  equilibrio: 'notar la piel más equilibrada con el tiempo',
  luminosidad: 'notar ese aspecto luminoso en el día a día',
  textura: 'suavizar la textura poco a poco',
  calma: 'sentir la piel calmada de principio a fin',
  completa: 'disfrutar de una rutina de cuidado completa'
};

var EXPERIENCE_LEVEL = {
  minima: 0,
  basica: 1,
  intermedia: 2,
  avanzada: 3
};

function buildStep(key, answers) {
  var description = STEP_BASE_DESCRIPTIONS[key];
  if (key === 'hidratante') {
    description = HIDRATANTE_BY_SKIN_TYPE[answers.skinType] || HIDRATANTE_BY_SKIN_TYPE.normal;
  } else if (key === 'serum') {
    var focus = SERUM_FOCUS_BY_CONCERN[answers.concern] || 'tu prioridad principal';
    description = 'Un sérum enfocado en ' + focus + ', aplicado antes de la hidratación.';
  }
  return { key: key, label: STEP_LABELS[key], description: description };
}

function generateRoutineRecommendation(answers) {
  var skinType = answers.skinType;
  var concern = answers.concern;
  var sensitivity = answers.sensitivity;
  var feel = answers.feel;
  var goal = answers.goal;
  var experienceLevel = EXPERIENCE_LEVEL[answers.experience];
  if (experienceLevel === undefined) experienceLevel = 1;

  // Sensibilidad alta, preocupaciones de barrera/sensibilidad, o una piel que
  // últimamente tira o cambia mucho: señales de que conviene una rutina más
  // suave y de apoyo a la barrera, con menos pasos activos.
  var caution =
    sensitivity === 'alta' ||
    concern === 'sensibilidad' ||
    concern === 'barrera' ||
    feel === 'tirante' ||
    feel === 'variable';

  var stepKeys = ['limpieza'];

  var includeTonico = !(experienceLevel === 0 && caution);
  if (includeTonico) stepKeys.push('tonico');

  var includeSerum = experienceLevel >= 2 && !caution;
  if (includeSerum) stepKeys.push('serum');

  stepKeys.push('hidratante');
  stepKeys.push('spf');

  var steps = stepKeys.map(function (key) {
    return buildStep(key, answers);
  });

  var skinTypePhrase = SKIN_TYPE_PHRASES[skinType] || 'tu piel';
  var concernPhrase = CONCERN_PHRASES[concern] || 'cuidar tu piel';
  var goalPhrase = GOAL_PHRASES[goal];

  var introText =
    'Por lo que nos cuentas, parece que tienes ' + skinTypePhrase +
    ' y que tu piel podría agradecer ' + concernPhrase +
    (goalPhrase ? ', para ' + goalPhrase : '') + '.';

  if (caution) {
    introText += ' Priorizaríamos suavidad, hidratación y apoyo de la barrera cutánea antes que ingredientes intensos.';
  } else if (steps.length <= 3) {
    introText += ' Priorizaríamos simplicidad: pocos pasos, bien elegidos.';
  } else if (steps.length === 4) {
    introText += ' Priorizaríamos constancia antes que sumar más pasos.';
  } else {
    introText += ' Priorizaríamos una rutina más completa, ya que parece que tu piel la puede acompañar bien.';
  }

  introText += ' Te proponemos empezar por estos pasos:';

  return { introText: introText, steps: steps };
}

/**
 * Product matching for each step. Uses only real, available products from the
 * store's skin-need collections (serialised by the section as JSON), never
 * invented data. A step with no suitable product is shown without one.
 */
var CONCERN_TO_COLLECTION = {
  manchas: 'manchas',
  imperfecciones: 'imperfecciones',
  poros: 'poros',
  hidratacion: 'hidratacion',
  antiedad: 'anti-edad',
  sensibilidad: 'piel-sensible',
  glow: 'glow',
  barrera: 'barrera-cutanea'
};

var STEP_FALLBACK_COLLECTION = {
  hidratante: 'hidratacion',
  spf: 'protector-solar'
};

function stepForProductType(type) {
  var t = (type || '').toLowerCase();
  if (/capilar|eye|ojos|body|corporal|lip|labio/.test(t)) return null;
  if (/sun|solar|spf/.test(t)) return 'spf';
  if (/cleans|limpiador|foam/.test(t)) return 'limpieza';
  if (/toner|t[oó]nico|mist|essence|esencia/.test(t)) return 'tonico';
  if (/serum|s[eé]rum|ampoule|ampolla/.test(t)) return 'serum';
  if (/cream|crema|balm|b[aá]lsamo/.test(t)) return 'hidratante';
  return null;
}

function matchProductsToSteps(steps, answers, catalog) {
  if (!catalog) return steps;
  var membership = {};
  var products = {};
  Object.keys(catalog).forEach(function (handle) {
    (catalog[handle] || []).forEach(function (product, index) {
      if (!product || !product.a) return;
      products[product.h] = product;
      membership[product.h] = membership[product.h] || {};
      membership[product.h][handle] = index;
    });
  });

  var concernCollection = CONCERN_TO_COLLECTION[answers.concern];
  var caution =
    answers.sensitivity === 'alta' ||
    answers.concern === 'sensibilidad' ||
    answers.concern === 'barrera' ||
    answers.feel === 'tirante' ||
    answers.feel === 'variable';
  var used = {};

  return steps.map(function (step) {
    var best = null;
    var bestScore = -1;
    var bestRank = Infinity;
    Object.keys(products).forEach(function (productHandle) {
      if (used[productHandle]) return;
      var product = products[productHandle];
      if (stepForProductType(product.ty) !== step.key) return;
      var inCollections = membership[productHandle];
      var gentle =
        'piel-sensible' in inCollections || 'barrera-cutanea' in inCollections || 'hidratacion' in inCollections;
      // Reactive skin: only suggest products the store lists as gentle/hydrating
      // (sunscreen excepted); otherwise the step is shown without a product.
      if (caution && step.key !== 'spf' && !gentle) return;
      var score = 0;
      if (concernCollection && concernCollection in inCollections) score += 4;
      if (caution && ('piel-sensible' in inCollections || 'barrera-cutanea' in inCollections)) score += 2;
      if (STEP_FALLBACK_COLLECTION[step.key] && STEP_FALLBACK_COLLECTION[step.key] in inCollections) score += 1;
      var rank = concernCollection && concernCollection in inCollections ? inCollections[concernCollection] : 999;
      if (score === 0) return;
      if (score > bestScore || (score === bestScore && rank < bestRank)) {
        best = product;
        bestScore = score;
        bestRank = rank;
      }
    });
    if (best) used[best.h] = true;
    return Object.assign({}, step, { product: best });
  });
}

var CONCERN_COLLECTIONS = {
  manchas: 'manchas',
  imperfecciones: 'imperfecciones',
  poros: 'poros',
  hidratacion: 'hidratacion',
  antiedad: 'anti-edad',
  sensibilidad: 'piel-sensible',
  glow: 'glow',
  barrera: 'barrera-cutanea'
};

(function () {
  'use strict';

  if (typeof window === 'undefined' || typeof customElements === 'undefined') {
    return;
  }

  class RoutineFinderController extends HTMLElement {
    connectedCallback() {
      this.answers = {};
      this.currentQuestionIndex = 0;

      this.introPanel = this.querySelector('[data-panel="intro"]');
      this.quizPanel = this.querySelector('[data-panel="quiz"]');
      this.resultPanel = this.querySelector('[data-panel="result"]');

      this.questions = Array.prototype.slice.call(this.querySelectorAll('[data-question]'));
      this.progressFill = this.querySelector('[data-progress-fill]');
      this.currentStepLabel = this.querySelector('[data-current-step]');
      this.totalStepsLabel = this.querySelector('[data-total-steps]');
      this.continueButton = this.querySelector('[data-action="continue"]');
      this.backButton = this.querySelector('[data-action="back"]');
      this.startButton = this.querySelector('[data-action="start"]');
      this.restartButton = this.querySelector('[data-action="restart"]');
      this.resultIntro = this.querySelector('[data-result-intro]');
      this.resultSteps = this.querySelector('[data-result-steps]');
      this.cartBlock = this.querySelector('[data-routine-cart]');
      this.addRoutineButton = this.querySelector('[data-action="add-routine"]');
      this.cartStatus = this.querySelector('[data-routine-cart-status]');
      this.exploreLink = this.querySelector('[data-explore-link]');
      this.routineProducts = [];

      if (this.totalStepsLabel) {
        this.totalStepsLabel.textContent = String(this.questions.length);
      }

      this._onStart = this.handleStart.bind(this);
      this._onContinue = this.handleContinue.bind(this);
      this._onBack = this.handleBack.bind(this);
      this._onRestart = this.handleRestart.bind(this);
      this._onOptionChange = this.handleOptionChange.bind(this);
      this._onAddRoutine = this.handleAddRoutine.bind(this);

      if (this.startButton) this.startButton.addEventListener('click', this._onStart);
      if (this.continueButton) this.continueButton.addEventListener('click', this._onContinue);
      if (this.backButton) this.backButton.addEventListener('click', this._onBack);
      if (this.restartButton) this.restartButton.addEventListener('click', this._onRestart);
      if (this.addRoutineButton) this.addRoutineButton.addEventListener('click', this._onAddRoutine);
      this.addEventListener('change', this._onOptionChange);

      this.showQuestion(0);
    }

    disconnectedCallback() {
      if (this.startButton) this.startButton.removeEventListener('click', this._onStart);
      if (this.continueButton) this.continueButton.removeEventListener('click', this._onContinue);
      if (this.backButton) this.backButton.removeEventListener('click', this._onBack);
      if (this.restartButton) this.restartButton.removeEventListener('click', this._onRestart);
      if (this.addRoutineButton) this.addRoutineButton.removeEventListener('click', this._onAddRoutine);
      this.removeEventListener('change', this._onOptionChange);
    }

    handleOptionChange(event) {
      if (!event.target.matches('.routine-finder__radio')) return;
      this.updateContinueState();
    }

    updateContinueState() {
      var currentQuestion = this.questions[this.currentQuestionIndex];
      if (!currentQuestion || !this.continueButton) return;
      var hasSelection = !!currentQuestion.querySelector('input:checked');
      this.continueButton.disabled = !hasSelection;
    }

    handleStart() {
      this.setPanel('quiz');
      this.showQuestion(0);
    }

    handleContinue() {
      var currentQuestion = this.questions[this.currentQuestionIndex];
      var checked = currentQuestion ? currentQuestion.querySelector('input:checked') : null;
      if (!checked) return;

      this.answers[currentQuestion.getAttribute('data-question')] = checked.value;

      if (this.currentQuestionIndex < this.questions.length - 1) {
        this.showQuestion(this.currentQuestionIndex + 1);
      } else {
        this.showResult();
      }
    }

    handleBack() {
      if (this.currentQuestionIndex > 0) {
        this.showQuestion(this.currentQuestionIndex - 1);
      } else {
        this.setPanel('intro');
      }
    }

    handleRestart() {
      this.answers = {};
      this.questions.forEach(function (question) {
        var checked = question.querySelector('input:checked');
        if (checked) checked.checked = false;
      });
      this.setPanel('intro');
      this.showQuestion(0);
    }

    showQuestion(index) {
      this.currentQuestionIndex = index;
      this.questions.forEach(function (question, questionIndex) {
        question.hidden = questionIndex !== index;
      });

      if (this.currentStepLabel) {
        this.currentStepLabel.textContent = String(index + 1);
      }
      if (this.progressFill) {
        var percent = ((index + 1) / this.questions.length) * 100;
        this.progressFill.style.width = percent + '%';
      }
      if (this.continueButton) {
        this.continueButton.textContent =
          index === this.questions.length - 1
            ? this.continueButton.getAttribute('data-final-label') || this.continueButton.textContent
            : this.continueButton.getAttribute('data-default-label') || this.continueButton.textContent;
      }

      this.updateContinueState();

      var legend = this.questions[index] && this.questions[index].querySelector('legend');
      if (legend) {
        legend.setAttribute('tabindex', '-1');
        legend.focus();
      }
    }

    getCatalog() {
      if (this.catalog !== undefined) return this.catalog;
      var node = this.querySelector('[data-routine-catalog]');
      try {
        this.catalog = node ? JSON.parse(node.textContent) : null;
      } catch (error) {
        this.catalog = null;
      }
      return this.catalog;
    }

    showResult() {
      var recommendation = generateRoutineRecommendation(this.answers);
      recommendation.steps = matchProductsToSteps(recommendation.steps, this.answers, this.getCatalog());
      this.renderResult(recommendation);
      this.setPanel('result');

      if (this.resultPanel) {
        this.resultPanel.focus();
      }
    }

    renderResult(recommendation) {
      if (this.resultIntro) {
        this.resultIntro.textContent = recommendation.introText;
      }
      if (this.resultSteps) {
        this.resultSteps.innerHTML = '';
        recommendation.steps.forEach(function (step) {
          var item = document.createElement('li');
          item.className = 'routine-finder__result-step';

          var title = document.createElement('span');
          title.className = 'routine-finder__result-step-title';
          title.textContent = step.label;

          var description = document.createElement('span');
          description.className = 'routine-finder__result-step-description';
          description.textContent = step.description;

          item.appendChild(title);
          item.appendChild(description);

          if (step.product) {
            var link = document.createElement('a');
            link.className = 'routine-finder__product';
            link.href = step.product.u;

            if (step.product.i) {
              var image = document.createElement('img');
              image.className = 'routine-finder__product-image';
              image.src = step.product.i;
              image.alt = step.product.t;
              image.width = 300;
              image.height = 300;
              image.loading = 'lazy';
              link.appendChild(image);
            }

            var info = document.createElement('span');
            info.className = 'routine-finder__product-info';

            var name = document.createElement('span');
            name.className = 'routine-finder__product-title';
            name.textContent = step.product.t;

            var price = document.createElement('span');
            price.className = 'routine-finder__product-price';
            price.textContent = step.product.p;

            var cta = document.createElement('span');
            cta.className = 'routine-finder__product-cta';
            cta.textContent = 'Ver producto';

            info.appendChild(name);
            info.appendChild(price);
            info.appendChild(cta);
            link.appendChild(info);
            item.appendChild(link);
          }

          this.resultSteps.appendChild(item);
        }, this);
      }
      this.prepareRoutineCart(recommendation.steps);
    }

    // One unit of each suggested product, each product only once.
    prepareRoutineCart(steps) {
      var seen = {};
      this.routineProducts = [];
      steps.forEach(function (step) {
        var product = step.product;
        if (!product || !product.v || seen[product.h]) return;
        seen[product.h] = true;
        this.routineProducts.push(product);
      }, this);

      if (this.exploreLink) {
        var collection = CONCERN_COLLECTIONS[this.answers.concern];
        this.exploreLink.href = collection ? '/collections/' + collection : this.exploreLink.getAttribute('href');
      }
      if (this.cartStatus) this.cartStatus.textContent = '';
      if (this.addRoutineButton) {
        this.addRoutineButton.disabled = false;
        this.addRoutineButton.removeAttribute('aria-busy');
      }
      if (this.cartBlock) this.cartBlock.hidden = this.routineProducts.length === 0;
    }

    // Adds the products one by one, so an unavailable product does not stop the rest.
    handleAddRoutine() {
      var products = this.routineProducts.slice();
      if (!products.length || !this.addRoutineButton) return;

      var button = this.addRoutineButton;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      if (this.cartStatus) this.cartStatus.textContent = 'Añadiendo tu rutina…';

      var added = [];
      var failed = [];
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

      var chain = products.reduce(function (promise, product) {
        return promise.then(function () {
          return fetch(root + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: product.v, quantity: 1 }] })
          })
            .then(function (response) {
              (response.ok ? added : failed).push(product);
            })
            .catch(function () {
              failed.push(product);
            });
        });
      }, Promise.resolve());

      chain.then(
        function () {
          button.removeAttribute('aria-busy');
          button.disabled = false;
          this.showCartMessage(added, failed);
          if (added.length) this.refreshCart();
        }.bind(this)
      );
    }

    showCartMessage(added, failed) {
      if (!this.cartStatus) return;
      var names = failed
        .map(function (product) {
          return product.t;
        })
        .join(', ');
      if (!added.length) {
        this.cartStatus.textContent =
          'Ahora mismo no hemos podido añadir estos productos. Puedes añadirlos desde su ficha.';
      } else if (failed.length) {
        this.cartStatus.textContent =
          'Hemos añadido el resto de tu rutina. No hay stock disponible ahora mismo de: ' + names + '.';
      } else {
        this.cartStatus.textContent = 'Tu rutina se ha añadido al carrito.';
      }
    }

    refreshCart() {
      var drawer = document.querySelector('cart-drawer');
      if (!drawer || typeof drawer.renderContents !== 'function') {
        window.location.href = (window.routes && window.routes.cart_url) || '/cart';
        return;
      }
      var sectionIds = drawer.getSectionsToRender().map(function (section) {
        return section.id;
      });
      fetch(window.location.pathname + '?sections=' + sectionIds.join(','))
        .then(function (response) {
          return response.json();
        })
        .then(function (sections) {
          drawer.classList.remove('is-empty');
          drawer.renderContents({ sections: sections });
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'routine-finder' });
          }
        })
        .catch(function () {
          window.location.href = (window.routes && window.routes.cart_url) || '/cart';
        });
    }

    setPanel(name) {
      [this.introPanel, this.quizPanel, this.resultPanel].forEach(function (panel) {
        if (!panel) return;
        panel.hidden = panel.getAttribute('data-panel') !== name;
      });
    }
  }

  if (!customElements.get('routine-finder')) {
    customElements.define('routine-finder', RoutineFinderController);
  }
})();

if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    generateRoutineRecommendation: generateRoutineRecommendation,
    matchProductsToSteps: matchProductsToSteps,
    stepForProductType: stepForProductType
  };
}
